import { getFunctions, httpsCallable } from 'firebase/functions';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import type {
  HelcimSubscription,
  HelcimPaymentPlan,
  HelcimCustomer,
  CreatePaymentPlanPayload,
} from '@rsmaids/shared/services/api/helcim/helcim';

const functions = getFunctions();

// -- Helcim recurring (plans & subscriptions) ---------------------------------

export async function getSubscriptionDetail(id: number, sub?: { paymentPlanId?: number; recurringAmount?: number; customerCode?: string; paymentMethod?: string }): Promise<Record<string, unknown> | null> {
  const fn = httpsCallable<{ id: number; paymentPlanId?: number; recurringAmount?: number; customerCode?: string; paymentMethod?: string }, { subscription: Record<string, unknown> | null }>(
    functions,
    'invoicingGetSubscription',
  );
  const result = await fn({ id, paymentPlanId: sub?.paymentPlanId, recurringAmount: sub?.recurringAmount, customerCode: sub?.customerCode, paymentMethod: sub?.paymentMethod });
  return result.data.subscription;
}

export async function getSubscriptions(params?: {
  customerCode?: string;
  paymentPlanId?: number;
  status?: string;
  skipEnrichment?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ subscriptions: HelcimSubscription[]; plans: HelcimPaymentPlan[] }> {
  const fn = httpsCallable<typeof params, { subscriptions: HelcimSubscription[]; plans: HelcimPaymentPlan[] }>(
    functions,
    'invoicingGetSubscriptions',
  );
  const result = await fn(params ?? {});
  return {
    subscriptions: result.data?.subscriptions ?? [],
    plans: result.data?.plans ?? [],
  };
}

export async function getPaymentPlans(params?: {
  status?: string;
}): Promise<{ plans: HelcimPaymentPlan[]; merchantTaxRate: number | null }> {
  const fn = httpsCallable<typeof params, { plans: HelcimPaymentPlan[]; merchantTaxRate: number | null }>(
    functions,
    'invoicingGetPaymentPlans',
  );
  const result = await fn(params ?? {});
  return { plans: result.data?.plans ?? [], merchantTaxRate: result.data?.merchantTaxRate ?? null };
}

export async function createPaymentPlan(payload: CreatePaymentPlanPayload): Promise<HelcimPaymentPlan | null> {
  const fn = httpsCallable<CreatePaymentPlanPayload, { plan: HelcimPaymentPlan | null }>(
    functions,
    'invoicingCreatePaymentPlan',
  );
  const result = await fn(payload);
  return result.data?.plan ?? null;
}

export async function updatePaymentPlan(id: number, payload: Partial<CreatePaymentPlanPayload>): Promise<HelcimPaymentPlan | null> {
  const fn = httpsCallable<{ id: number } & Partial<CreatePaymentPlanPayload>, { plan: HelcimPaymentPlan | null }>(
    functions,
    'invoicingUpdatePaymentPlan',
  );
  const result = await fn({ id, ...payload });
  return result.data?.plan ?? null;
}

export async function deletePaymentPlan(id: number): Promise<void> {
  const fn = httpsCallable<{ id: number }, { success: boolean }>(
    functions,
    'invoicingDeletePaymentPlan',
  );
  await fn({ id });
}

export async function createCustomPricePlan(params: {
  basePlanId: number;
  price: number;
  title?: string;
}): Promise<{ plan: HelcimPaymentPlan; subscribeUrl: string | null }> {
  const fn = httpsCallable<typeof params, { plan: HelcimPaymentPlan; subscribeUrl: string | null }>(
    functions,
    'invoicingCreateCustomPricePlan',
  );
  const result = await fn(params);
  return result.data;
}


export async function createSubscription(params: {
  paymentPlanId: number;
  customerCode: string;
  dateActivated: string;   // YYYY-MM-DD
  paymentMethod?: 'card' | 'bank';
  recurringAmount?: number;
  setupAmount?: number;
}): Promise<HelcimSubscription> {
  const fn = httpsCallable<typeof params, { subscription: HelcimSubscription }>(
    functions,
    'invoicingCreateSubscription',
  );
  const result = await fn(params);
  return result.data?.subscription as HelcimSubscription;
}

export async function manageSubscription(
  subscriptionId: number,
  action: 'pause' | 'resume' | 'cancel' | 'delete',
): Promise<void> {
  const fn = httpsCallable<{ subscriptionId: number; action: string }, { success: boolean }>(
    functions,
    'invoicingManageSubscription',
  );
  await fn({ subscriptionId, action });
}

export async function updateSubscriptionFields(
  subscriptionId: number,
  fields: { recurringAmount?: number; paymentPlanId?: number; maxCycles?: number; dateActivated?: string; paymentMethod?: 'card' | 'bank' },
): Promise<HelcimSubscription | null> {
  const fn = httpsCallable<
    { subscriptionId: number; fields: typeof fields },
    { subscription: HelcimSubscription }
  >(functions, 'invoicingUpdateSubscription');
  const result = await fn({ subscriptionId, fields });
  return result.data?.subscription ?? null;
}

export async function getHelcimTransactions(params?: {
  dateStart?: string;
  dateEnd?: string;
  customerCode?: string;
  page?: number;
  limit?: number;
  type?: string;
}): Promise<{ transactions: Record<string, unknown>[]; hasMore: boolean }> {
  const fn = httpsCallable<typeof params, { transactions: Record<string, unknown>[]; hasMore: boolean }>(
    functions,
    'invoicingGetCardTransactions',
  );
  const result = await fn(params ?? {});
  return { transactions: result.data?.transactions ?? [], hasMore: result.data?.hasMore ?? false };
}

export async function getHelcimAchTransactions(params?: {
  dateStart?: string;
  dateEnd?: string;
  customerCode?: string;
  page?: number;
  limit?: number;
}): Promise<{ transactions: Record<string, unknown>[]; hasMore: boolean }> {
  const fn = httpsCallable<typeof params, { transactions: Record<string, unknown>[]; hasMore: boolean }>(
    functions,
    'invoicingGetAchTransactions',
  );
  const result = await fn(params ?? {});
  return { transactions: result.data?.transactions ?? [], hasMore: result.data?.hasMore ?? false };
}

export async function getAllHelcimCustomers(): Promise<HelcimCustomer[]> {
  const fn = httpsCallable<Record<string, never>, { customers: HelcimCustomer[] }>(
    functions,
    'invoicingGetAllCustomers',
  );
  const result = await fn({});
  return result.data?.customers ?? [];
}

// Fires a `{ ping: true }` call to every function that supports the ping
// short-circuit, so their containers cold-start once, in the background,
// right after login — well before the user could plausibly open any
// specific screen (Payments, Subscriptions, etc.) that needs them for real.
// Fire-and-forget: failures are swallowed since this is purely a warm-up,
// not a data fetch — nothing downstream depends on its result. Call this
// once at app startup (see AuthContext.tsx), not from individual screens —
// firing it alongside a screen's own real data call gives it no lead time
// and provides no benefit (confirmed: this is what the old per-screen
// warmHelcimCustomer() calls were doing before this replaced them).
export function warmAllFunctions(): void {
  const targets = ['invoicingGetCustomer', 'invoicingGetAchTransactions', 'invoicingGetCardTransactions'];
  for (const name of targets) {
    httpsCallable(functions, name)({ ping: true }).catch(() => {});
  }
}

export async function getHelcimCustomer(customerId?: string, helcimNumericId?: number): Promise<Record<string, unknown> | null> {
  const fn = httpsCallable<{ customerId?: string; helcimNumericId?: number }, { customer: Record<string, unknown> }>(
    functions,
    'invoicingGetCustomer',
  );
  const result = await fn({ ...(customerId ? { customerId } : {}), ...(helcimNumericId ? { helcimNumericId } : {}) });
  return result.data?.customer ?? null;
}

// enrich (default true) fetches each result's cards/bank accounts too — 2 extra
// Helcim round-trips per matching customer. Pass false for a live-typing
// search list where only name/email/code are shown; leave it true when
// looking up one known customer by exact code, where the payment methods are
// actually needed.
export async function searchHelcimCustomers(search: string, enrich: boolean = true): Promise<HelcimCustomer[]> {
  const fn = httpsCallable<{ search: string; enrich?: boolean }, { customers: HelcimCustomer[] }>(
    functions,
    'invoicingSearchCustomers',
  );
  const result = await fn({ search, enrich });
  return result.data?.customers ?? [];
}

// -- Wix Bookings services (line-item templates) ------------------------------

export interface WixService {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
}

export async function getWixServices(): Promise<WixService[]> {
  const response = await callAdminApi('queryServices', 'services', {});

  const raw: unknown[] =
    (response as Record<string, unknown[]>)?._items ??
    (response as Record<string, unknown[]>)?.items ??
    [];

  return raw.map((item) => {
    const svc = item as Record<string, unknown>;
    const payment = svc.payment as Record<string, unknown> | undefined;
    const rateType = payment?.rateType as string | undefined;
    let price = 0;
    if (rateType === 'FIXED' || rateType === 'FIXED_PRICE') {
      const fixed = payment?.fixed as Record<string, unknown> | undefined;
      price = parseFloat(String(fixed?.price ?? fixed?.amount ?? 0)) || 0;
    } else if (rateType === 'VARIED') {
      const varied = payment?.varied as Record<string, unknown> | undefined;
      price = parseFloat(String(varied?.minPrice ?? 0)) || 0;
    }
    return {
      id: String(svc._id ?? ''),
      name: String(svc.name ?? svc.title ?? ''),
      description: String(svc.description ?? svc.tagLine ?? ''),
      price,
      currency: 'USD',
    };
  });
}
